import React from "react";
import { Link } from "react-router-dom";
import Starfield from "@/components/Starfield";
import { ArrowLeft } from "lucide-react";

const CONTACT = "support@stellarsteps.online";

const Section = ({ title, children }) => (
  <section className="mb-7">
    <h2 className="font-heading text-lg md:text-xl font-bold text-cyan-300 mb-2">{title}</h2>
    <div className="text-slate-300 text-sm leading-relaxed space-y-2">{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  return (
    <div className="relative min-h-screen bg-[#0B0D1B] text-white overflow-hidden">
      <Starfield />
      <div className="relative z-10 max-w-3xl mx-auto p-5 sm:p-8 pb-28">
        <Link to="/" data-testid="privacy-back-link" className="text-slate-400 hover:text-white flex items-center gap-1 text-sm mb-5">
          <ArrowLeft className="w-4 h-4" /> Back to Stellar Steps
        </Link>
        <h1 data-testid="privacy-title" className="font-heading text-4xl sm:text-5xl font-bold mb-1">Privacy Policy</h1>
        <p className="text-slate-400 mb-8 text-sm">Data controller: Stellar Steps · Last updated June 2026</p>

        <Section title="Who we are">
          <p>Stellar Steps is a classroom progress tracker for teachers and their pupils. This policy explains what data we collect, why, how long we keep it, and how you can have it removed. For any privacy question, contact <a className="text-cyan-300 underline" href={`mailto:${CONTACT}`}>{CONTACT}</a>.</p>
        </Section>

        <Section title="What data we collect">
          <p><strong>Teachers:</strong> the name you type when creating an account and an automatically generated 6-digit sign-in code. No email address or password is required to use the app.</p>
          <p><strong>Pupils:</strong> a first name (or nickname) chosen by the pupil, a chosen rocket colour/style, and their progress through class stages and optional quiz results. Pupils receive a 6-digit return code. <strong>We never ask pupils for an email address, surname, date of birth, or any contact details.</strong></p>
          <p><strong>Essential storage:</strong> we store small values in your browser to keep you signed in and remember your ship. We do not use advertising or third-party tracking cookies.</p>
        </Section>

        <Section title="Why we collect it (lawful basis)">
          <p>We process this data solely to provide the classroom tracking service to the school/teacher, on the basis of the legitimate interests of the teacher and school in tracking learning progress. Pupil accounts contain the minimum data needed and no contact information.</p>
        </Section>

        <Section title="How long we keep it">
          <p>Pupil names and progress are retained until the earlier of: (a) the teacher deleting the pupil or the class, or (b) the auto-delete date the teacher sets for the class (for example, the end of the school year), after which the class and all its pupil data are permanently erased automatically.</p>
          <p>If no auto-delete date is set, data is kept until the teacher deletes it.</p>
        </Section>

        <Section title="Safeguarding & child safety">
          <p>Stellar Steps is designed to be safe for classroom use:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Pupils join a session using a class code only — no email or account registration.</li>
            <li>There are no public profiles and no way to search for pupils; a pupil is only reachable inside their own class.</li>
            <li>There is no direct messaging between pupils anywhere in the app.</li>
            <li>Student-facing text (such as ship names) is filtered for profanity.</li>
            <li>No advertising is shown to any user.</li>
            <li>Teachers can remove a pupil from a session instantly at any time.</li>
          </ul>
        </Section>

        <Section title="Who we share it with">
          <p>We do not sell data and do not share it with advertisers. Data is stored on our hosting and database provider purely to run the service. A managed email provider is used only to deliver data-request notifications to us.</p>
        </Section>

        <Section title="Your rights (GDPR)">
          <p>You may request access to, correction of, or deletion of personal data. To request deletion, use our <Link to="/data-request" className="text-cyan-300 underline">Data Deletion Request form</Link> or email <a className="text-cyan-300 underline" href={`mailto:${CONTACT}`}>{CONTACT}</a>. Teachers can also delete pupils or whole classes themselves at any time from Mission Control.</p>
        </Section>

        <Section title="Contact">
          <p>Questions about this policy or your data: <a className="text-cyan-300 underline" href={`mailto:${CONTACT}`}>{CONTACT}</a>.</p>
        </Section>

        <p className="text-slate-500 text-xs">See also our <Link to="/terms" className="text-cyan-300 underline">Terms of Use</Link>.</p>
      </div>
    </div>
  );
}
